<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :submit="handleSubmitFormEditRemooc"
        size="large"
        :rules="rules"
        :title="$t('labels.remoocModule.edit')"
        :loading="loading"
        @close="closeOverSide"
        @undo="undo()">
        <hts-card :title="$t('labels.generalInformation')">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <hts-select-remooc-category
                    v-model="dataFormEditRemooc.remoocType"
                    v-model:name="dataFormEditRemooc.nameTypeRemooc"
                    required
                    prop="remoocType"
                    @change="handleInputVehicleLoad" />
                <hts-form-input
                    v-model="dataFormEditRemooc.licensePlate"
                    :label="$t('labels.remoocModule.licensePlate')"
                    :color="color"
                    required
                    name="licensePlate"
                    prop="licensePlate" />
                <hts-form-input
                    v-model="dataFormEditRemooc.manufacture"
                    :label="$t('labels.remoocModule.manufacture')"
                    :color="color"
                    required
                    name="manufacture"
                    prop="manufacture" />
                <hts-form-input
                    v-model="dataFormEditRemooc.load"
                    :label="$t('labels.vehicleLoadByKg')"
                    :color="color"
                    type="number"
                    required
                    name="load"
                    prop="load" />
                <hts-form-input
                    v-model="dataFormEditRemooc.weight"
                    :label="$t('labels.weightRemoocKG')"
                    :color="color"
                    type="number"
                    required
                    name="weight"
                    prop="weight" />
                <hts-form-date
                    v-model="dataFormEditRemooc.buyingTime"
                    :label="$t('labels.vehicleModule.buyingTime')"
                    :color="color"
                    :required="true"
                    type="year"
                    name="buyingTime"
                    prop="buyingTime" />
                <hts-form-input
                    v-model="dataFormEditRemooc.price"
                    :label="$t('labels.remoocModule.price')"
                    :color="color"
                    type="number"
                    required
                    name="price"
                    prop="price" />
                <!--
                  <hts-form-select
                  v-model="dataFormEditRemooc.coveringContainerLevel"
                  :label="$t('labels.vehicleModule.coveringContainerLevel')"
                  required
                  :items="coveringContainerLevel"
                  key-id="value"
                  key-label="label"
                  :color="color"
                  name="coveringContainerLevel"
                  prop="coveringContainerLevel" />
                -->
                <hts-form-input
                    v-model="dataFormEditRemooc.description"
                    :label="$t('labels.description')"
                    :color="color"
                    name="description"
                    prop="description" />
            </div>
        </hts-card>
        <hts-card
            v-if="checkPermission(layoutPermissions.remooc_edit_pricing)"
            :color="highlightInput">
            <template #title>
                <div>
                    <div class="w-full flex justify-start my-2">
                        <span class="mr-4">
                            {{ $t('labels.vehicleModule.administration') }}
                        </span>

                        <hi-arrow-path-icon
                            class="w-6 h-6"
                            :class="{
                                'text-warning-500': !dataFormEditRemooc.approvedByManagement,
                                'text-success-500': dataFormEditRemooc.approvedByManagement
                            }" />

                        <p
                            v-if="!dataFormEditRemooc.approvedByManagement"
                            class="text-warning-500">
                            {{ $t('labels.waittingApprove') }}
                        </p>

                        <p
                            v-if="dataFormEditRemooc.approvedByManagement"
                            class="text-success-500">
                            {{ $t('labels.approved') }}
                        </p>
                    </div>
                </div>
            </template>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <hts-form-input
                    v-model="dataFormEditRemooc.depreciation"
                    :label="$t('labels.depreciationPeriodRemoocByYear')"
                    type="number"
                    :color="highlightInput || color"
                    required
                    name="depreciationPeriodRemoocByYear"
                    prop="depreciation" />
                <hts-form-input
                    v-model="dataFormEditRemooc.sellingPrice"
                    :label="$t('labels.remoocModule.sellingPrice')"
                    type="number"
                    :color="highlightInput || color"
                    required
                    name="sellingPrice"
                    prop="sellingPrice" />
            </div>
        </hts-card>
        <hts-card :title="$t('labels.truckInformation')">
            <hts-form-checkbox
                v-model="dataFormEditRemooc.isTowingContainer"
                class="mt-5"
                :items="[{ label: $t('labels.qualificationDragContainer'), value: false }]"
                prop="isTowingContainer"
                @change="handleCheckbox" />
            <hts-form-select
                v-if="dataFormEditRemooc.isTowingContainer"
                v-model="dataFormEditRemooc.towingCapacity"
                :label="$t('labels.suitableContainerType')"
                :required="true"
                :items="towingCapacityList"
                key-id="value"
                key-label="label"
                :color="color"
                multiple
                name="suitableContainerType"
                prop="containerType" />
        </hts-card>
    </hts-drawer>
</template>

<script setup lang="ts">
import form, { IRemooc } from '../imports/remooc'

import { $lodash } from '#imports'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
const route = useRoute()
const id: string = route.query.id ? String(route.query.id) : ''

const highlightInput = computed(() => {
  if (id && !dataFormEditRemooc?.approvedByManagement) {
    return 'orange'
  }
})

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  },
  remoocInfo: {
    type: Object
  },
  checkDisabledEdit: {
    type: Boolean,
    default: true
  }
})

const emits = defineEmits(['close', 'refreshTables'])
const dataFormEditRemooc = reactive<IRemooc>({ ...form.initRemooc })

const loading = ref(true)
const visible = ref(false)

const openEditRemooc = (data?: any) => {
  visible.value = true
  loading.value = true
  Object.assign(dataFormEditRemooc, form.initRemooc)
  $api($url.vehicle.remooc.getById, { uriParams: { ID: data.id || '' } }).then((response: any) => {
    const data = response?.data?.value?.data || {}
    Object.assign(dataFormEditRemooc, data)
    dataFormEditRemooc.nameTypeRemooc = dataFormEditRemooc?.remoocCategoryDetail?.name || ''
    nextTick(() => {
      loading.value = false
    })
  })
}
defineExpose({
  openEditRemooc
})
const closeOverSide = () => {
  visible.value = false
}

const undo = () => {
  Object.assign(dataFormEditRemooc, { ...form.initRemooc })
}

const towingCapacityList = [
  {
    value: '20',
    label: $t('labels.vehicleModule.20ft')
  },
  {
    value: '40',
    label: $t('labels.vehicleModule.40ft')
  },
  {
    value: '45',
    label: $t('labels.vehicleModule.45ft')
  }
]

const handleCheckbox = (val: any) => {
  if (!val) {
    dataFormEditRemooc.towingCapacity = []
  }
}

const handleSubmitFormEditRemooc = async () => {
  const api = $url.vehicle.remooc.edit
  const data: any = await $api(api, {
    uriParams: { id: props.remoocInfo?._id },
    body: {
      ...dataFormEditRemooc,
      remoocType: dataFormEditRemooc.remoocType || props.remoocInfo?.remoocCategoryDetail._id
    }
  })
  const resData: any = data?.data?.value
  if (resData?.success) {
    Object.assign(dataFormEditRemooc, { ...form.initRemooc })
    emits('refreshTables')
    process.client && $toast().success($t('labels.editSuccess'))
    undo()
    closeOverSide()
  }
}

const handleInputVehicleLoad = $lodash.debounce((value: Array<{ [key: string]: any }>) => {
  if (value.length) {
    dataFormEditRemooc.depreciation = value?.[0]?.depreciationPeriodRemooc || null
    dataFormEditRemooc.sellingPrice = value?.[0]?.priceLiquidation || null
  }
}, 300)

const yearNow = new Date().getFullYear()

const rules = computed(() => {
  const data: { [key: string]: any } = {}
  data.buyingTime = 'min_value:1971|max_value:' + yearNow
  data.depreciation = 'min_value:1|integer'

  if (typeof dataFormEditRemooc.sellingPrice === 'number' && typeof dataFormEditRemooc.price === 'number') {
    if (checkPermission(layoutPermissions.remooc_edit_pricing)) {
      data.sellingPrice = `required|validationCustom:${dataFormEditRemooc.sellingPrice < dataFormEditRemooc.price}--Giá thanh lý phải nhỏ hơn giá mua`
    }

    data.price = `required|validationCustom:${dataFormEditRemooc.sellingPrice < dataFormEditRemooc.price}--Giá mua phải lớn hơn giá thanh lý`
  }

  return data
})

watch(
  () => dataFormEditRemooc.nameTypeRemooc,
  (val) => {
    if (loading.value) {
      return
    }
    if (val === $t('labels.vehicleModule.20ft')) {
      dataFormEditRemooc.towingCapacity = ['20']
    }
    if (val === $t('labels.vehicleModule.40ft')) {
      dataFormEditRemooc.towingCapacity = ['20', '40']
    }
    if (val === $t('labels.vehicleModule.45ft')) {
      dataFormEditRemooc.towingCapacity = ['20', '40', '45']
    }
  }
)
</script>
